import React from 'react';
import { ArrowLeft } from 'lucide-react';
import '../../styles/realsignal-theme.css';

interface PrivacyPolicyPageProps {
    onBack: () => void;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    border: '#1C2E42',
};

export const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onBack }) => {
    return (
        <div className="min-h-screen overflow-auto" style={{ background: colors.background }}>
            <div className="max-w-3xl mx-auto px-6 py-12" style={{ maxWidth: '800px' }}>
                <button
                    onClick={onBack}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: colors.textSecondary,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        marginBottom: '2rem',
                    }}
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Sign In</span>
                </button>

                <h1
                    style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: colors.textPrimary,
                        marginBottom: '0.5rem',
                    }}
                >
                    Privacy Policy
                </h1>
                <p
                    style={{
                        color: colors.textMuted,
                        marginBottom: '2rem',
                    }}
                >
                    Last updated: January 2026
                </p>

                <div
                    style={{
                        background: colors.backgroundLight,
                        borderRadius: '1rem',
                        padding: '2rem',
                        color: colors.textSecondary,
                        lineHeight: 1.7,
                    }}
                >
                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            1. Information We Collect
                        </h2>
                        <p style={{ marginBottom: '1rem' }}>
                            We collect information you provide directly to us, including your name,
                            email address, and payment information when you create an account or
                            subscribe to our services.
                        </p>
                        <p>
                            We also automatically collect certain information when you use
                            RealSignal, including your IP address, browser type, operating system,
                            and usage patterns within the application.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            2. How We Use Your Information
                        </h2>
                        <p>
                            We use the information we collect to provide, maintain, and improve our
                            services; process transactions and send related information; send you
                            technical notices, updates, and support messages; respond to your
                            comments and questions; and monitor and analyze trends, usage, and
                            activities in connection with our services.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            3. Data Sources
                        </h2>
                        <p>
                            RealSignal aggregates publicly available real estate data from county
                            records, court filings, and other public sources. This data is used to
                            provide distress intelligence insights. We do not collect or store
                            personal information about property owners beyond what is publicly
                            available in these records.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            4. Information Sharing
                        </h2>
                        <p>
                            We do not sell, trade, or otherwise transfer your personal information
                            to third parties except as described in this policy. We may share
                            information with service providers who assist us in operating our
                            platform, processing payments, or analyzing how our service is used.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            5. Data Security
                        </h2>
                        <p>
                            We implement appropriate technical and organizational measures to
                            protect your personal information against unauthorized access,
                            alteration, disclosure, or destruction. This includes encryption of data
                            in transit and at rest, regular security assessments, and access
                            controls.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            6. Data Retention
                        </h2>
                        <p>
                            We retain your personal information for as long as your account is
                            active or as needed to provide you services. We will retain and use your
                            information as necessary to comply with our legal obligations, resolve
                            disputes, and enforce our agreements.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            7. Your Rights
                        </h2>
                        <p>
                            You have the right to access, correct, or delete your personal
                            information. You may also request a copy of your data or ask us to
                            restrict processing of your information. To exercise these rights,
                            please contact us at privacy@realsignal.app.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            8. Cookies and Tracking
                        </h2>
                        <p>
                            We use cookies and similar tracking technologies to track activity on
                            our service and hold certain information. You can instruct your browser
                            to refuse all cookies or to indicate when a cookie is being sent.
                        </p>
                    </section>

                    <section style={{ marginBottom: '2rem' }}>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            9. Changes to This Policy
                        </h2>
                        <p>
                            We may update this Privacy Policy from time to time. We will notify you
                            of any changes by posting the new Privacy Policy on this page and
                            updating the "Last updated" date.
                        </p>
                    </section>

                    <section>
                        <h2
                            style={{
                                color: colors.textPrimary,
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                marginBottom: '1rem',
                            }}
                        >
                            10. Contact Us
                        </h2>
                        <p>
                            If you have any questions about this Privacy Policy, please contact us
                            at privacy@realsignal.app.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
